import type { TellescopeModelFromName } from "@tellescope/tanstack-query";
import type { CustomFields } from "@tellescope/types-models";
import { RelatedContactFormResult } from "./AddRelatedContactForm";
import { parseDateStringAnswer } from "../../utils";

const toStringFields = (fields: CustomFields): Record<string, string> =>
  Object.entries(fields).reduce<Record<string, string>>((acc, [label, value]) => {
    if (typeof value === "string") acc[label] = value;
    return acc;
  }, {});

const parseDateOfBirth = (dateOfBirth: string): Date | undefined => {
  const parsed = parseDateStringAnswer(dateOfBirth);
  if (parsed) return new Date(parsed.year, parsed.month - 1, parsed.day);

  // A stored ISO date is read as a calendar date, not an instant, so the day does not shift
  // for viewers behind UTC.
  const isoDate = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateOfBirth);
  if (isoDate) return new Date(Number(isoDate[1]), Number(isoDate[2]) - 1, Number(isoDate[3]));

  const fallback = new Date(dateOfBirth);
  return isNaN(fallback.getTime()) ? undefined : fallback;
};

export const convertContactToRelatedContact = (
  contact: Partial<TellescopeModelFromName<"endusers">>,
  relatedEnduserId: string
): RelatedContactFormResult | undefined => {
  const relationship =
    contact.relationships?.find(({ id }) => id === relatedEnduserId)?.type ?? contact.relationships?.[0]?.type;
  if (!relationship) return undefined;
  return {
    fname: contact.fname ?? "",
    lname: contact.lname,
    relationship,
    dateOfBirth: contact.dateOfBirth ? parseDateOfBirth(contact.dateOfBirth) : undefined,
    email: contact.email ?? "",
    phoneNumber: contact.phone,
    fields: contact.fields && toStringFields(contact.fields),
  };
};
